import React, { useEffect, useState } from "react";
import {
  ThinDropdownWithSearch,
  ThinTextField,
} from "../common/GeneralComponent";
import { toast } from "react-toastify";
import { fetchData } from "../../utils/functions";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

const TransportUsage = ({ transportUsageList = [], DqaRejected }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const [selectedPurpose, setSelectedPurpose] = useState({});
  const [selectedMode, setSelectedMode] = useState({});
  const [specifyOther, setSpecifyOther] = useState({}); 
  const [errors, setErrors] = useState({});
  const [isDqaApproved, setIsDqaApproved] = useState(false);
  const [isDqaRejected, setIsDqaRejected] = useState(false);

  const modeOptions = [
    { value: "None", label: "None" },
    { value: "Car / Motorcycle", label: "Car / Motorcycle" },
    { value: "Public Transport", label: "Public Transport" },
    { value: "Rickshaw / Chingchi", label: "Rickshaw / Chingchi" },
    { value: "Walk", label: "Walk" },
    { value: "Donkey Cart", label: "Donkey Cart" },
    { value: "Bicycle", label: "Bicycle" },
    { value: "Not Applicable", label: "Not Applicable" },
    { value: "Others", label: "Others" },
  ];

  const purposeOptions = [
    { value: "work", label: "Work" },
    { value: "education", label: "Education" },
    { value: "health", label: "Health" },
    { value: "market", label: "Market" },
    { value: "social_visits", label: "Social Visits" },
  ];

  // Fetch household info to check DQA approval
  const fetchHouseholdInformationDetails = async (householdInfoId) => {
    if (!householdInfoId) return;
    try {
      const response = await fetchData(
        `householdinformation-show/${householdInfoId}`,
        "GET",
        null,
        {},
        {},
        navigate,
        "/household",
        dispatch
      );
      if (response?.data) {
        setIsDqaApproved(
          response.data.verified_from_dqa === 1 ||
            response.data.verified_from_dqa === "1"
        );
        setIsDqaRejected(
          response.data.verified_from_dqa === 2 || response.data.verified_from_dqa === "2"
        )
      }
    } catch (error) {
      console.error("Error fetching household information details:", error);
    }
  };

  useEffect(() => {
    fetchHouseholdInformationDetails(id);
  }, [id]);

  // Seed local state from props
  useEffect(() => {
    const initialPurpose = {};
    const initialMode = {};
    const initialSpecifyOther = {};
    transportUsageList.forEach((record) => {
      const recordId = record.household_transport_use_id;
      initialPurpose[recordId] = record.purpose || "";
      initialMode[recordId] = record.mode || "";
      initialSpecifyOther[recordId] = record.specify_other_mode || ""; // ✅ seed from DB
    });
    setSelectedPurpose(initialPurpose);
    setSelectedMode(initialMode);
    setSpecifyOther(initialSpecifyOther);
    setErrors({});
  }, [transportUsageList]);

  const handlePurposeChange = (recordId, value) => {
    setSelectedPurpose((prev) => ({ ...prev, [recordId]: value }));
    setErrors((prev) => ({
      ...prev,
      [recordId]: { ...(prev[recordId] || {}), purpose: undefined },
    }));
  };

  const handleModeChange = (recordId, value) => {
    setSelectedMode((prev) => ({ ...prev, [recordId]: value }));
    // Clear "specify other" if not Others
    if (value !== "Others") {
      setSpecifyOther((prev) => ({ ...prev, [recordId]: "" }));
    }
    setErrors((prev) => ({
      ...prev,
      [recordId]: { ...(prev[recordId] || {}), mode: undefined },
    }));
  };

  const handleSpecifyOtherChange = (recordId, value) => {
    setSpecifyOther((prev) => ({ ...prev, [recordId]: value }));
    setErrors((prev) => ({
      ...prev,
      [recordId]: { ...(prev[recordId] || {}), specify_other: undefined },
    }));
  };

  const purposeUsedElsewhere = (recordId, purpose) =>
    Boolean(purpose) &&
    transportUsageList.some(
      (item) =>
        String(item.household_transport_use_id) !== String(recordId) &&
        selectedPurpose[item.household_transport_use_id] === purpose
    );

  const purposeOptionsFor = (recordId) =>
    purposeOptions.filter(
      (option) =>
        option.value === selectedPurpose[recordId] ||
        !purposeUsedElsewhere(recordId, option.value)
    );

  const validateAll = () => {
    const next = {};
    transportUsageList.forEach((item) => {
      const id = item.household_transport_use_id;
      const e = {};
      if (!selectedPurpose[id]) e.purpose = "Purpose of Travel is Required.";
      else if (purposeUsedElsewhere(id, selectedPurpose[id]))
        e.purpose = "This purpose is already used in another transport record.";
      if (!selectedMode[id]) e.mode = "Mode of transport is Required.";
      if (selectedMode[id] === "Others" && !specifyOther[id]) {
        e.specify_other = "Please specify the other mode.";
      }
      if (Object.keys(e).length) next[id] = e;
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validateAll()) {
      return;
    }

    try {
      const payload = transportUsageList.map((item) => ({
        household_transport_use_id: item.household_transport_use_id,
        purpose: selectedPurpose[item.household_transport_use_id] || "",
        mode: selectedMode[item.household_transport_use_id] || "",
        specify_other_mode: specifyOther[item.household_transport_use_id] || "",
      }));

      const res = await fetchData(
        `household-transport-update/${id}`,
        "PUT",
        payload
      );

      if (res?.success) {
        toast.success("Transport data updated successfully");
      } else {
        toast.error(res?.message || "Update failed");
      }
    } catch (error) {
      console.error("Update error", error);
      toast.error("Something went wrong");
    }
  };

  const hasRows =
    Array.isArray(transportUsageList) && transportUsageList.length > 0;

  const Err = (recordId, field) =>
    errors?.[recordId]?.[field] ? (
      <p className="text-[0.9rem] text-red-600 mt-2">
        {errors[recordId][field]}
      </p>
    ) : null;

  return (
    <div className="bg-white">
      {/* <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          {isDqaApproved && (
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          )}
        </div>
      </div> */}
      <div className="flex justify-between items-center mb-3">
        {isDqaApproved && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Transport Use
            </h2>
            <span className="bg-blue-100 text-blue-800 px-1 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          </>
        )}
        {isDqaRejected && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Transport Use
            </h2>

            <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">
              Rejected from DQA
            </span>
          </>
        )}
      </div>

      {hasRows ? (
        <>
          {transportUsageList.map((record, index) => {
            const recordId = record.household_transport_use_id;
            const serialNumber = index + 1;
            return (
              <div
                key={recordId}
                className="mb-8 p-4 border border-gray-200 rounded-lg bg-white shadow"
              >
                <h3 className="text-lg font-medium text-gray-800 mb-4">
                  Transport Record #{serialNumber}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <ThinDropdownWithSearch
                      label="Purpose of Travel"
                      options={purposeOptionsFor(recordId)}
                      value={selectedPurpose[recordId] || ""}
                      onChange={(value) => handlePurposeChange(recordId, value)}
                      required
                      disabled={isDqaApproved || isDqaRejected}
                    />
                    {Err(recordId, "purpose")}
                  </div>

                  <div>
                    <ThinDropdownWithSearch
                      label="Mode of Transport"
                      options={modeOptions}
                      value={selectedMode[recordId] || ""}
                      onChange={(value) => handleModeChange(recordId, value)}
                      required
                      disabled={isDqaApproved || isDqaRejected}
                    />
                    {Err(recordId, "mode")}
                  </div>
                  {selectedMode[recordId] === "Others" && (
                    <div className="mt-4">
                      <ThinTextField
                        label="Specify Other Mode"
                        value={specifyOther[recordId] || ""}
                        onChange={(e) =>
                          handleSpecifyOtherChange(recordId, e.target.value)
                        }
                        disabled={isDqaApproved || isDqaRejected}
                        placeholder="Enter other transport mode"
                        style={{ width: "100%" }}
                      />
                      {Err(recordId, "specify_other")}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {!isDqaApproved && !isDqaRejected && (
            <div className="mt-8 flex justify-end">
              <button
                className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-2 rounded-lg transition duration-200 font-medium"
                onClick={handleSave}
              >
                Update
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-500 italic text-lg">
            No transport usage records for this household
          </p>
        </div>
      )}
    </div>
  );
};

export default TransportUsage;
